import { useAccount, useMsal } from "@azure/msal-react";
import { AccountInfo } from "@azure/msal-browser";
import React from "react";
import { useLoggedInUser } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useUpsertUserByEmail } from "../../login/hooks/useUpsertUserByEmail";
import statusTypes from "@coworker/enums/statusTypes";
import { ErrorType } from "../constants/errorTypes";
import { HttpError } from "@coworker/http-client";

type UserState = {
  isActive: boolean;
  isLoading: boolean;
  errorType: ErrorType | null;
  isError: boolean;
};
export const useAuthorizeUser = () => {
  const { accounts } = useMsal();
  const msalAccount = useAccount(accounts[0] || {}) as AccountInfo;

  const { setLoggedInUser, resetLoggedInUser } = useLoggedInUser();

  const initialUserState = {
    isActive: false,
    isLoading: true,
    errorType: null,
    isError: false,
  } as UserState;
  const [userState, setUserState] = React.useState(initialUserState);

  let email =
    msalAccount?.idTokenClaims?.preferred_username ?? msalAccount?.username;
  if (email?.split("@")[1]?.toLowerCase() === "ikea.com") {
    email = email.replace("ikea", "ingka.ikea");
  }
  const uid = msalAccount.idTokenClaims?.oid ?? "";
  const isQueryEnabled = !!email && !!uid;
  const {
    data: userData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useUpsertUserByEmail(email, uid);

  React.useEffect(() => {
    if (!isQueryEnabled) return;
    if (!isLoading && !isFetching && !isError) {
      const hasStoreAssigned = Boolean(userData?.store_id);

      if (
        userData &&
        userData.status === statusTypes.ACTIVE &&
        hasStoreAssigned
      ) {
        setUserState({
          isActive: true,
          errorType: null,
          isLoading: false,
          isError: false,
        });
        setLoggedInUser(userData);
      } else if (userData && userData.status !== statusTypes.ACTIVE) {
        // User exists in DB but is INACTIVE — should not be allowed to self-provision
        setUserState({
          isActive: false,
          errorType: ErrorType.FORBIDDEN,
          isLoading: false,
          isError: false,
        });
        resetLoggedInUser();
      } else {
        // User has no store assigned yet or doesn't exist — needs self-provisioning
        setUserState({
          isActive: false,
          errorType: ErrorType.NOTFOUND,
          isLoading: false,
          isError: false,
        });
        resetLoggedInUser();
      }
    } else if (isError) {
      const httpStatus = error instanceof HttpError ? error.status : null;
      const errorType =
        httpStatus === 403
          ? ErrorType.FORBIDDEN
          : httpStatus === 404
            ? ErrorType.NOTFOUND
            : httpStatus === 401
              ? ErrorType.UNAUTHORIZED
              : ErrorType.INTERNALERROR;
      setUserState({
        isActive: false,
        errorType,
        isLoading: false,
        isError: true,
      });
      resetLoggedInUser();
    }
  }, [
    isLoading,
    userData,
    setLoggedInUser,
    isError,
    error,
    isFetching,
    resetLoggedInUser,
    isQueryEnabled,
  ]);
  return userState;
};
